import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { openCustomerPortal } from '../../utils/cn';
import { fetchGoogleReviews, syncGoogleReviews, calculateReviewStats, fetchPlaceMetadata } from '../../services/reviewsService';
import { useGoogleStats } from '../../hooks/useGoogleStats';

// Last-known live Google aggregate — shown for the brief window before
// useGoogleStats resolves, and as a safety net if Supabase is unreachable.
// MUST stay in sync with the FALLBACK_TOTAL_REVIEWS constant used by the
// homepage SocialProofSection / TrustBridge / RecentInstallsSection so
// the same number renders site-wide on first paint.
const FALLBACK_TOTAL_REVIEWS = 32;
const FALLBACK_OVERALL_RATING = 5.0;

const StarRating = ({ rating, size = 16 }) => (
  <div className="flex items-center space-x-0.5">
    {Array.from({ length: 5 }, (_, i) => (
      <svg
        key={i}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill={i < rating ? '#FBBF24' : 'none'}
        stroke={i < rating ? '#FBBF24' : '#D1D5DB'}
        strokeWidth="1.5"
        className="flex-shrink-0"
      >
        <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
      </svg>
    ))}
  </div>
);

const ReviewCard = ({ review }) => {
  const initials = review?.authorName
    ? review?.authorName?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)
    : '?';

  const dateLabel = review?.relativeTimeDescription || (review?.time
    ? new Date(review.time * 1000)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short' })
    : '');

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300 flex flex-col gap-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {review?.profilePhotoUrl ? (
            <img
              src={review?.profilePhotoUrl}
              alt={`${review?.authorName} profile photo`}
              className="w-11 h-11 rounded-full object-cover flex-shrink-0"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <div
            className="w-11 h-11 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-700 font-bold text-sm"
            style={{ display: review?.profilePhotoUrl ? 'none' : 'flex' }}
          >
            {initials}
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm leading-tight">{review?.authorName}</p>
            <p className="text-xs text-gray-400 mt-0.5">{dateLabel}</p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StarRating rating={review?.rating} size={14} />
        </div>
      </div>
      {review?.text && (
        <p className="text-gray-600 text-sm leading-relaxed line-clamp-4">
          "{review?.text}"
        </p>
      )}
      <div className="flex items-center gap-1.5 text-xs text-gray-400">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="flex-shrink-0">
          <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.33 0 9.25-3.65 9.25-9.09 0-1.15-.15-1.81-.15-1.81z" fill="#4285F4"/>
        </svg>
        <span>Google Review</span>
      </div>
    </div>
  );
};

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [googleTotalCount, setGoogleTotalCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [syncMessage, setSyncMessage] = useState(null);

  // LIVE aggregate from the same Supabase source the homepage SocialProof
  // section, TrustBridge, RecentInstallsSection, and GoogleReviewsBadge all
  // share (google_place_stats → fetchPlaceStats → useGoogleStats). Single
  // source of truth — the count rendered in the hero must always match
  // the homepage card and the rest of the site. Hook has module-level
  // caching, so this re-uses any in-flight fetch.
  const { stats: liveStats } = useGoogleStats();
  const heroTotalReviews = liveStats?.totalReviews ?? FALLBACK_TOTAL_REVIEWS;
  const heroOverallRating = liveStats?.overallRating ?? FALLBACK_OVERALL_RATING;
  const heroFormattedTotal = Math.floor(heroTotalReviews).toLocaleString('en-US');

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      setSyncing(true);
      setError(null);
      // Auto-sync from Google on every page load
      let syncResult = null;
      try {
        syncResult = await syncGoogleReviews();
        if (syncResult?.total_ratings) {
          setGoogleTotalCount(syncResult?.total_ratings);
        }
      } catch {
        // Sync failed silently — still load cached reviews from DB
      }
      const [data, metadata] = await Promise.all([
        fetchGoogleReviews(),
        fetchPlaceMetadata(),
      ]);
      setReviews(data);
      setStats(calculateReviewStats(data));
      if (metadata?.totalRatingCount) {
        setGoogleTotalCount(metadata?.totalRatingCount);
      }
    } catch (err) {
      setError('Unable to load reviews. Please try again later.');
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  const handleSync = async () => {
    try {
      setSyncing(true);
      setSyncMessage(null);
      setError(null);
      const result = await syncGoogleReviews();
      setSyncMessage(result?.message || 'Reviews synced successfully!');
      if (result?.total_ratings) {
        setGoogleTotalCount(result?.total_ratings);
      }
      await loadReviews();
    } catch (err) {
      setError('Failed to sync reviews from Google. Please check your API key and Place ID in Supabase Edge Function secrets.');
    } finally {
      setSyncing(false);
    }
  };

  const aggregateRatingSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://openbooq.com/#localbusiness",
    "name": "OpenbooQ",
    "url": "https://openbooq.com",
    "telephone": "+1-937-910-8791",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "1 E 4th St",
      "addressLocality": "Dayton",
      "addressRegion": "OH",
      "postalCode": "45402",
      "addressCountry": "US"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": stats?.overallRating?.toString() || "5.0",
      "reviewCount": (googleTotalCount || stats?.totalReviews)?.toString() || "32",
      "bestRating": "5",
      "worstRating": "1"
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://openbooq.com" },
      { "@type": "ListItem", "position": 2, "name": "Reviews", "item": "https://openbooq.com/reviews" }
    ]
  };

  return (
    <>
      <Helmet>
        <title>Customer Reviews | OpenbooQ</title>
        <meta
          name="description"
          content="Read real Google reviews from satisfied OpenbooQ customers. See why homeowners trust us for transparent pricing, top-tier shingles, and expert roof installations."
        />
        <script type="application/ld+json">{JSON.stringify(aggregateRatingSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>
      <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <Header />

        {/* ═══ HERO — Pass 1 (structure + content, no entrance animation)
            Full-bleed photo bg (smiling homeowner with phone) + dark navy
            overlay. Cyan eyebrow, proof-led headline, 3-stat aggregate
            panel (LIVE rating + count from useGoogleStats — same source
            as the homepage SocialProof "Verified Google reviews" stat —
            and a hardcoded "100%" five-star tile matching the homepage
            label), then a single-line subhead. Mobile pt clears the
            floating header. */}
        <section
          className="relative overflow-hidden pt-48 sm:pt-36 lg:pt-44 pb-16 lg:pb-24"
          style={{ backgroundColor: '#0a1628' }}
        >
          {/* Layer 1 — blurred cover copy of the photo fills the entire
              section. scale(1.2) hides the soft edges of the blur so
              the sides read as a continuous frosted extension of the
              centered photo. Blur dialed up to 55px so the peripherals
              read as one smooth wash that the eye can blend with the
              sharp center — too sharp a blur and the brain reads the
              seam where the contain photo ends. */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              backgroundImage: `url('/assets/images/reviews-hero.webp')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              filter: 'blur(55px)',
              transform: 'scale(1.2)',
            }}
          />
          {/* Layer 2 — sharp photo as an <img> element so the feathering
              mask anchors to the photo's actual pixel edges (a CSS
              background-image mask would anchor to the parent div
              instead, which would leave hard photo edges in the middle
              of a wide section). h-full w-auto = scale by height,
              maintain aspect — equivalent to background-size: contain
              but with proper bounds for the mask. The radial-gradient
              mask feathers all 4 photo edges so the photo dissolves
              into the blurred backdrop rather than meeting it at a
              hard line. */}
          <img
            src="/assets/images/reviews-hero.webp"
            alt=""
            aria-hidden="true"
            className="absolute inset-y-0 left-1/2 -translate-x-1/2 h-full w-auto max-w-none pointer-events-none select-none"
            style={{
              maskImage:
                'radial-gradient(ellipse 60% 75% at center, black 55%, transparent 100%)',
              WebkitMaskImage:
                'radial-gradient(ellipse 60% 75% at center, black 55%, transparent 100%)',
            }}
          />
          {/* Layer 3 — dark navy overlay keeps white text legible. Sits
              above both photo layers so blur and contain are both tinted.
              Opacity dialed back from 86%→68% so the blurred photo on
              the sides reads as photo, not as flat navy. Text remains
              legible thanks to the dark areas in the source photo and
              the glass stat tiles' own backdrops. */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{
              background:
                'linear-gradient(135deg, rgba(10, 22, 40, 0.68) 0%, rgba(14, 30, 54, 0.60) 100%)',
            }}
          />
          {/* Cyan ambient glow center-top */}
          <div
            className="absolute pointer-events-none"
            aria-hidden="true"
            style={{
              top: '15%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '50%',
              height: '50%',
              background: 'radial-gradient(ellipse, rgba(7,176,233,0.18) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          />

          <div className="relative max-w-5xl mx-auto px-5 lg:px-8 text-center text-white">
            {/* Eyebrow pill — cyan, matches the homepage SocialProof voice */}
            <div className="mb-6 flex justify-center">
              <div
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.14em]"
                style={{
                  background: 'rgba(7, 176, 233, 0.18)',
                  border: '1px solid rgba(7, 176, 233, 0.45)',
                  color: '#6cc7ff',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: '#10B981' }}
                  aria-hidden="true"
                />
                Real Homeowners. Real Results.
              </div>
            </div>

            {/* Proof-led headline */}
            <h1
              className="font-black leading-[1.05] tracking-tight mb-5"
              style={{
                fontSize: 'clamp(34px, 5vw, 56px)',
                fontFamily: "'Poppins', 'Montserrat', sans-serif",
                letterSpacing: '-0.025em',
              }}
            >
              Rated {heroOverallRating.toFixed(1)} by real homeowners.
            </h1>

            {/* Subhead — supporting line */}
            <p
              className="mx-auto mb-10 max-w-2xl leading-relaxed"
              style={{
                fontSize: 'clamp(15px, 1.4vw, 18px)',
                color: 'rgba(255, 255, 255, 0.85)',
              }}
            >
              Verified Google reviews from real OpenbooQ Inc. customers — pulled live, never paid for, never edited.
            </p>

            {/* Aggregate stats panel — 3 glass tiles. Stacks on mobile,
                3-across on sm+. LIVE values from useGoogleStats. */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 max-w-3xl mx-auto">
              {/* Tile 1 — overall rating + stars */}
              <div
                className="rounded-2xl p-5 lg:p-6"
                style={{
                  background: 'rgba(255, 255, 255, 0.10)',
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                }}
              >
                <div
                  className="font-black text-white leading-none mb-2"
                  style={{
                    fontSize: 'clamp(36px, 4.2vw, 52px)',
                    letterSpacing: '-0.03em',
                  }}
                >
                  {heroOverallRating.toFixed(1)}
                </div>
                <div className="flex justify-center mb-2">
                  <StarRating rating={Math.round(heroOverallRating)} size={18} />
                </div>
                <p className="text-[11px] sm:text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255, 255, 255, 0.78)' }}>
                  Google rating
                </p>
              </div>

              {/* Tile 2 — live verified review count */}
              <div
                className="rounded-2xl p-5 lg:p-6"
                style={{
                  background: 'rgba(255, 255, 255, 0.10)',
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                }}
              >
                <div
                  className="font-black text-white leading-none mb-3"
                  style={{
                    fontSize: 'clamp(36px, 4.2vw, 52px)',
                    letterSpacing: '-0.03em',
                  }}
                >
                  {heroFormattedTotal}
                </div>
                <p className="text-[11px] sm:text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255, 255, 255, 0.78)' }}>
                  Verified Google reviews
                </p>
              </div>

              {/* Tile 3 — 100% five-star (matches homepage SocialProof label) */}
              <div
                className="rounded-2xl p-5 lg:p-6"
                style={{
                  background: 'rgba(255, 255, 255, 0.10)',
                  border: '1px solid rgba(255, 255, 255, 0.22)',
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)',
                }}
              >
                <div
                  className="font-black text-white leading-none mb-3"
                  style={{
                    fontSize: 'clamp(36px, 4.2vw, 52px)',
                    letterSpacing: '-0.03em',
                  }}
                >
                  100%
                </div>
                <p className="text-[11px] sm:text-xs font-bold uppercase tracking-widest" style={{ color: 'rgba(255, 255, 255, 0.78)' }}>
                  Five-star ratings
                </p>
              </div>
            </div>
          </div>
        </section>

        <main className="flex-1 pt-32 pb-16 px-4">
          <div className="max-w-6xl mx-auto">

            {/* Hero */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-full px-4 py-1.5 mb-4">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.33 0 9.25-3.65 9.25-9.09 0-1.15-.15-1.81-.15-1.81z" fill="#4285F4"/>
                </svg>
                <span className="text-blue-700 text-xs font-semibold tracking-wide uppercase">Google Reviews</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                What Our Customers Say
              </h1>
              <p className="text-lg text-gray-500 max-w-xl mx-auto">
                Real reviews from real homeowners — pulled directly from Google.
              </p>
            </div>

            {/* Stats Bar */}
            {!loading && stats && stats?.totalReviews > 0 && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-10 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-5xl font-bold text-gray-900">{stats?.overallRating}</div>
                    <StarRating rating={Math.round(stats?.overallRating)} size={18} />
                    <p className="text-xs text-gray-400 mt-1">
                      {googleTotalCount ? `${googleTotalCount?.toLocaleString()} reviews` : `${stats?.totalReviews} reviews`}
                    </p>
                  </div>
                  <div className="hidden sm:block w-px h-16 bg-gray-100" />
                  <div className="space-y-1.5">
                    {stats?.ratingDistribution?.map((item) => (
                      <div key={item?.stars} className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 w-3">{item?.stars}</span>
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="#FBBF24" stroke="#FBBF24" strokeWidth="1.5">
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                        </svg>
                        <div className="w-28 bg-gray-100 rounded-full h-1.5">
                          <div
                            className="bg-yellow-400 h-1.5 rounded-full transition-all duration-500"
                            style={{ width: `${item?.percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-400 w-8">{item?.percentage}%</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col items-center gap-2">
                  <div className="text-2xl font-bold text-blue-600">{stats?.satisfactionRate}%</div>
                  <p className="text-xs text-gray-500 text-center">4+ star reviews</p>
                </div>
              </div>
            )}

            {/* Sync Button */}
            <div className="flex justify-end mb-6 gap-3 items-center">
              {syncMessage && (
                <span className="text-sm text-green-600 font-medium">{syncMessage}</span>
              )}
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-2 text-sm bg-white border border-gray-200 hover:border-blue-400 hover:text-blue-600 text-gray-600 px-4 py-2 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                <Icon name={syncing ? 'Loader2' : 'RefreshCw'} size={14} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing...' : 'Sync from Google'}
              </button>
            </div>

            {/* Error State */}
            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-8 flex items-start gap-3">
                <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 6 })?.map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-11 h-11 rounded-full bg-gray-100" />
                      <div className="flex-1 space-y-2">
                        <div className="h-3 bg-gray-100 rounded w-3/4" />
                        <div className="h-2 bg-gray-100 rounded w-1/2" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 bg-gray-100 rounded" />
                      <div className="h-2 bg-gray-100 rounded w-5/6" />
                      <div className="h-2 bg-gray-100 rounded w-4/6" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State — no reviews yet */}
            {!loading && reviews?.length === 0 && !error && (
              <div className="text-center py-20">
                <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Star" size={36} className="text-blue-400" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">No Reviews Yet</h2>
                <p className="text-gray-500 mb-6 max-w-md mx-auto">
                  Click "Sync from Google" to pull in your latest Google reviews. Make sure your API key and Place ID are configured in Supabase Edge Function secrets.
                </p>
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 disabled:opacity-50"
                >
                  <Icon name={syncing ? 'Loader2' : 'RefreshCw'} size={16} className={syncing ? 'animate-spin' : ''} />
                  {syncing ? 'Syncing...' : 'Sync Reviews Now'}
                </button>
              </div>
            )}

            {/* Reviews Grid */}
            {!loading && reviews?.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {reviews?.map((review) => (
                  <ReviewCard key={review?.id} review={review} />
                ))}
              </div>
            )}

            {/* CTA */}
            <div className="mt-16 text-center">
              <div className="bg-blue-600 rounded-2xl p-8 md:p-12 text-white">
                <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready for Your New Roof?</h2>
                <p className="text-blue-100 mb-6 max-w-md mx-auto">
                  Join hundreds of happy homeowners. Get your transparent roof price today.
                </p>
                <Button
                  size="lg"
                  onClick={() => openCustomerPortal()}
                  className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-semibold rounded-lg shadow-lg transition-all duration-200"
                >
                  Get Roof Price
                </Button>
              </div>
            </div>

            {/* SEO Body Copy */}
            <div className="mt-16 pt-10 border-t border-gray-100 text-left max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">What OpenbooQ Customers Are Saying</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Homeowners trust OpenbooQ for transparent roof replacement pricing and professional installation. Our customers consistently highlight three things: the ease of getting an accurate price online, the quality of the shingles installed, and the professionalism of our certified contractor network.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Unlike traditional roofing companies that rely on high-pressure sales tactics and opaque pricing, OpenbooQ gives homeowners full visibility into material costs, labor rates, and project timelines before a single nail is driven.{' '}
                <Link to="/how-it-works" className="text-blue-600 hover:underline font-medium">Learn how it works</Link> or{' '}
                <Link to="/instant-estimate" className="text-blue-600 hover:underline font-medium">get your free roof price</Link>.
              </p>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Reviews;